<template>
  <div ref="riverNamePanel" :key="updTooltip">
    <div v-for="(value, key) in showRiverGeoJson" v-bind:key="key">
      <div
        v-if="
          isWarn(value.features[0].properties.type)
            ? showRiverWarn(value.features[0].properties.type)
            : showRiverName()
        "
      >
        <l-geo-json :geojson="value" :options="optionGeoJson"> </l-geo-json>
        <!-- マーカーはツールチップ表示位置となるため必要 -->
        <l-marker
          :lat-lng="[value.position[1], value.position[0]]"
          :options="optionMarker"
          :opacity="0"
        >
          <l-tooltip
            :options="createOptionTooltip(value.features[0].properties)"
            @mouseover="mouseOverTooltip($event, value)"
            @mouseleave="mouseLeaveTooltip($event, value)"
            @click="clickRwTooltip(value.features[0].properties)"
            >{{ value.dispName }}</l-tooltip
          >
        </l-marker>
      </div>
    </div>
  </div>
</template>
<!-- ----------------------------------------------- -->
<script>
import { LGeoJson, LMarker, LTooltip } from "vue2-leaflet";
import Util from "../../../lib/util.js";
import MapUtil from "../../../lib/mapUtil.js";
import mixin from "../../../lib/mixin.js";
import WebStorage from "../../../lib/webStorage.js";
import L from "leaflet";

/**
 * 川の防災情報 地図画面 河川レイヤー
 * RiverLayer.vue
 * @author LIS
 */

export default {
  name: "river-layer",
  mixins: [mixin],
  components: {
    LGeoJson,
    LMarker,
    LTooltip,
  },
  /** プロパティ部 */
  props: {
    mapNum: {
      type: Number,
      default: 0,
    },
    mapObj: {
      type: Object,
    },
  },
  /** 初期化部 */
  mounted() {
    // 表示範囲の河川GeoJsonを取得
    this.getRiverGeoJson();
  },
  data() {
    return {
      showRiverGeoJson: [],
      updTooltip: 0,
      tmpRiverLatLonList: [],
      culcDecimal: Math.pow(10, 9), // 小数点誤差をなくすための計算に用いる
      mapObjName: "mapObj" + this.mapNum,
    };
  },
  /** 算出部 */
  computed: {
    /** 地図設定情報 */
    mapConfig: {
      get() {
        return this.$store.state.map.mapConfig;
      },
    },
    /** 設定情報 */
    config: {
      get() {
        return this.$store.state.app.config;
      },
    },
    /** モード */
    isPcMode: {
      get() {
        return this.$store.state.app.mode == "pc";
      },
    },
    /** 表示範囲の河川GeoJson */
    riverGeoJson: {
      get() {
        return this.$store.state.map.riverGeoJson;
      },
    },
    /** 表示範囲の河川GeoJson */
    riverWarnGeoJson: {
      get() {
        if (
          this.$store.state.RiverWarn.rwGeoJson &&
          this.$store.state.app.appTime
        ) {
          // テレメータGeoJsonの中から該当日時のデータを抽出
          const dateIndex = this.$store.state.RiverWarn.rwGeoJson.findIndex(
            (info) => info.date == this.baseTime
          );
          if (dateIndex >= 0) {
            //同一区間コードに複数の発表が存在する場合、河川ラインに合わせて洪水予報を優先する
            let rwGeoJsonInfo = [];
            this.$store.state.RiverWarn.rwGeoJson[
              dateIndex
            ].rwGeoJsonInfo.forEach((crnt) => {
              let exist = rwGeoJsonInfo.find((val) => {
                return (
                  val.features[0].properties.section_id ===
                  crnt.features[0].properties.section_id
                );
              });
              if (exist) {
                let idx = rwGeoJsonInfo.indexOf(exist);
                if (
                  exist.features[0].properties.type === 11 &&
                  crnt.features[0].properties.type === 10
                ) {
                  rwGeoJsonInfo[idx] = crnt;
                }
                return rwGeoJsonInfo;
              } else {
                return rwGeoJsonInfo.push(crnt);
              }
            });
            return rwGeoJsonInfo;
          }
        }
        return [];
      },
    },
    concatRiverGeoJson: {
      get() {
        return Array.concat(this.riverGeoJson, this.riverWarnGeoJson);
      },
    },
    /** 基準時間を計算 */
    baseTime: {
      get() {
        let returnTime;
        if (this.$store.state.radar.radarNumber !== 4) {
          // 1枚表示の場合は、appストアの観測日時を基準にする、
          returnTime = this.$store.state.app.appTime;
        } else {
          // 4枚表示の場合は、レーダ観測日時を基準にする
          let radarTime;
          if (this.$store.state.radar.radarType == "xrain") {
            radarTime = this.$store.state.radar.radarXrainDate;
          } else {
            radarTime = this.$store.state.radar.radarCbandDate;
          }
          returnTime = MapUtil.calcBaseTime(
            radarTime,
            this.mapNum,
            this.$store.state.radar.radarInterval
          );
        }
        // 5分or10分間隔に切り捨て、文字列変換
        let roundTime = Util.roundMinutes(
          returnTime,
          this.$store.state.app.config.appConfig.keepDaysJsonFiles,
          this.$store.state.app.appTimeRoundRw
        );
        return Util.formatDate(roundTime, "YYYY-MM-DD HH:mm");
      },
    },
    /** 表示市町村取得時刻 */
    getTownsAcqTime: {
      get() {
        return this.$store.state.map.townsAcqTime;
      },
    },
    /** 地図画面中心地 */
    center: {
      get() {
        return this.$store.state.map.center;
      },
    },
    /** 地図画面最小緯度経度 */
    minCoord: {
      get() {
        return this.$store.state.map.minCoord;
      },
    },
    /** 地図画面最大緯度経度 */
    maxCoord: {
      get() {
        return this.$store.state.map.maxCoord;
      },
    },
    par1pxLatLon: {
      get() {
        let latPerPx = 0;
        let lonPerPx = 0;

        // 特定の緯度経度をピクセル座標に変換
        if (this.mapObj) {
          // 特定の緯度経度をピクセル座標に変換
          var pixelPoint = this.mapObj.project(this.center, this.zoom);
          // 1ピクセル加算したピクセル座標を取得
          var newPixelPoint = L.point(pixelPoint.x + 1, pixelPoint.y + 1);
          // 新しいピクセル座標を緯度経度に変換
          var newLatLng = this.mapObj.unproject(newPixelPoint, this.zoom);
          // 1ピクセルあたりの緯度経度の差を計算
          latPerPx =
            Math.abs(
              newLatLng.lat * this.culcDecimal -
                this.center[0] * this.culcDecimal
            ) / this.culcDecimal;
          lonPerPx =
            Math.abs(
              newLatLng.lng * this.culcDecimal -
                this.center[1] * this.culcDecimal
            ) / this.culcDecimal;
        }
        return [latPerPx, lonPerPx];
      },
    },
    /** 1pxあたりの緯度 */
    par1pxLat: {
      get() {
        return this.par1pxLatLon[0];
      },
    },
    /** 1pxあたりの経度 */
    par1pxLon: {
      get() {
        return this.par1pxLatLon[1];
      },
    },
    /** 河川名表示の際の地図の最小緯度経度（地図上表示コンポーネントを除く領域） */
    mapMinLatLon: {
      get() {
        // 表示設定等のボタン領域からpadding-topを除く
        let removeHeight = this.getComponentsSize().settingPanelHeight - 8;
        // PCモードの場合はズームコントロール領域（約60px）を設定 [自動取得すると幅が狭いため手動で設定]
        let removeWidth = this.isPcMode ? 60 : 0;
        let lat =
          (this.minCoord[0] * this.culcDecimal +
            removeHeight * (this.par1pxLat * this.culcDecimal)) /
          this.culcDecimal;
        let lon =
          (this.minCoord[1] * this.culcDecimal +
            removeWidth * (this.par1pxLon * this.culcDecimal)) /
          this.culcDecimal;
        return [lat, lon];
      },
    },
    /** 河川名表示の際の地図の最大緯度経度（地図上表示コンポーネントを除く領域） */
    mapMaxLatLon: {
      get() {
        // お知らせ分の領域
        let removeHeight = this.getComponentsSize().infoHeight;
        if (!this.isPcMode) {
          // MBの場合はメニューボタン分の領域を除外領域として追加
          removeHeight += this.getComponentsSize().sideMenuHeight;
        }
        let lat =
          (this.maxCoord[0] * this.culcDecimal -
            removeHeight * (this.par1pxLat * this.culcDecimal)) /
          this.culcDecimal;
        let lon = this.maxCoord[1];
        return [lat, lon];
      },
    },
    /** 河川名表示の際の地図の中心緯度経度（地図上表示コンポーネントを除く領域） */
    mapCenterLatLon: {
      get() {
        let lat =
          (this.mapMinLatLon[0] * this.culcDecimal +
            this.diffLatLon[0] * this.culcDecimal) /
          this.culcDecimal;
        let lon =
          (this.mapMinLatLon[1] * this.culcDecimal +
            this.diffLatLon[1] * this.culcDecimal) /
          this.culcDecimal;
        return [lat, lon];
      },
    },
    /** 中心から最大・最小緯度経度の差分 */
    diffLatLon: {
      get() {
        let latDiff =
          (this.mapMaxLatLon[0] * this.culcDecimal -
            this.mapMinLatLon[0] * this.culcDecimal) /
          2 /
          this.culcDecimal;
        let lonDiff =
          (this.mapMaxLatLon[1] * this.culcDecimal -
            this.mapMinLatLon[1] * this.culcDecimal) /
          2 /
          this.culcDecimal;
        return [latDiff, lonDiff];
      },
    },
    /** 中心から一定の割合をおいた最小緯度経度 */
    limitMinLatLon: {
      get() {
        let lat =
          (this.mapMinLatLon[0] * this.culcDecimal +
            Math.abs(
              this.diffLatLon[0] * this.culcDecimal * this.mapRangeRate
            )) /
          this.culcDecimal;
        let lon =
          (this.mapMinLatLon[1] * this.culcDecimal +
            Math.abs(
              this.diffLatLon[1] * this.culcDecimal * this.mapRangeRate
            )) /
          this.culcDecimal;
        return [lat, lon];
      },
    },
    /** 中心から一定の割合をおいた最大緯度経度 */
    limitMaxLatLon: {
      get() {
        let lat =
          (this.mapMaxLatLon[0] * this.culcDecimal -
            Math.abs(
              this.diffLatLon[0] * this.culcDecimal * this.mapRangeRate
            )) /
          this.culcDecimal;
        let lon =
          (this.mapMaxLatLon[1] * this.culcDecimal -
            Math.abs(
              this.diffLatLon[1] * this.culcDecimal * this.mapRangeRate
            )) /
          this.culcDecimal;
        return [lat, lon];
      },
    },
    /** zoomレベル */
    zoom: {
      get() {
        return this.$store.state.map.zoom;
      },
    },
    /** 地図中心からの一定距離の割合 */
    mapRangeRate: {
      get() {
        //デフォルト設定は50%とする
        let pct = 50;
        // config.jsonの設定値が数値かつ0～100の場合、設定値を使用する
        if (
          typeof this.mapConfig.mapRangePct === "number" &&
          0 <= this.mapConfig.mapRangePct &&
          this.mapConfig.mapRangePct <= 100
        ) {
          pct = this.mapConfig.mapRangePct;
        }
        return (100 - pct) * 0.01;
      },
    },
    /** geoJSON用オプション */
    optionGeoJson() {
      return {
        color: "",
        weight: "",
        opacity: "",
      };
    },
    /** マーカー用オプション */
    optionMarker() {
      return {
        // 未設定の場合ツールチップより前面にマーカーがきてしまい、ホバーが効かなくなるため
        pane: "riverNameMarkerPane",
      };
    },
    /** お知らせの数 */
    infoCnt() {
      return (
        this.$store.state.info.emergency.length +
        this.$store.state.info.any.length
      );
    },
    /** webストレージ編集日時 */
    editWebStorageDate() {
      return this.$store.state.map.editWebStorageDate;
    },
    /** 表示グループ設定 */
    viewGroup() {
      return this.$store.state.map.viewGroup;
    },
    /** 河川予警報表示設定 */
    viewRiverWarn() {
      return this.$store.state.map.viewRiverWarn;
    },
    /** 表示コンテンツ */
    getContent() {
      return this.$store.state.app.content;
    },
  },
  methods: {
    /** 表示範囲の河川GeoJsonを取得 */
    getRiverGeoJson() {
      let prefTwnList = [];
      if (this.zoom < this.mapConfig.zoomReferenceTowns) {
        if (this.$store.state.map.prefs) {
          prefTwnList = this.$store.state.map.prefs;
          this.$store.dispatch("map/getRiverGeoJson", prefTwnList);
        }
      } else {
        if (this.$store.state.map.towns) {
          prefTwnList = this.$store.state.map.towns;
          this.$store.dispatch("map/getRiverGeoJson", prefTwnList);
        }
      }
    },
    /** 表示用の河川ラインgeoJSONを作成 */
    createRiverGeoJson(rvrGeoJson) {
      // 平常河川ライン
      let newRvrGeoJson = [];
      // 洪水予報等発表時の河川ライン
      let newRvrWrnGeoJson = [];
      // 表示する河川GeoJSON、退避用緯度経度リストの初期化
      this.showRiverGeoJson = [];
      this.tmpRiverLatLonList = [];
      if (this.riverGeoJson.length > 0) {
        rvrGeoJson.forEach((rvr) => {
          // 表示開始ズームレベルを満たす河川の場合または発表情報が存在する河川の場合
          if (
            this.zoom >= rvr.features[0].properties.from_disp_zoom ||
            this.isWarn(rvr.features[0].properties.type)
          ) {
            // 地図表示内に存在する緯度経度を抽出する
            let filterLatLons = this.filterRiverLatLonList(
              rvr.features[0].geometry.coordinates,
              rvr.features[0].properties.W05_004,
              rvr.features[0].geometry.type
            );
            // 地図表示内に緯度経度が存在する場合
            if (filterLatLons.length > 0) {
              let updRvr = JSON.parse(JSON.stringify(rvr));
              // geoJSONのpositionを更新
              updRvr.position = this.getPostionRiverName(
                filterLatLons,
                rvr.features[0].properties.W05_004
              );
              // 表示名の追加
              updRvr.dispName = this.isWarn(rvr.features[0].properties.type)
                ? rvr.features[0].properties.section_name
                : rvr.features[0].properties.W05_004;
              // ツールチップホバー用プロパティを追加
              updRvr.isHover = false;
              // 発表情報の有無で格納先を分岐
              if (this.isWarn(rvr.features[0].properties.type)) {
                newRvrWrnGeoJson.push(updRvr);
              } else {
                newRvrGeoJson.push(updRvr);
              }
            }
          }
        });
      }
      // 発表情報が存在する場合は発表情報の河川のみ表示する
      this.showRiverGeoJson = Array.from([
        ...newRvrWrnGeoJson,
        ...newRvrGeoJson,
      ]);
      // 画面更新
      this.updTooltip += 1;
    },
    /**
     * 地図表示内の緯度経度の抽出
     */
    filterRiverLatLonList(latLonList, rvrName, type) {
      // ツールチップ表示領域_河川名表示緯度経度から左側の幅
      let rvrNmWidthL = this.setRvrNmWidthL(rvrName);
      // ツールチップ表示領域_河川名表示緯度経度から右側の幅（右のほうが若干大きいため左側と分けて用意）
      let rvrNmWidthR = this.setRvrNmWidthR(rvrName);
      // ツールチップ表示領域_高さ
      let rvrNmHeight = this.setRvrNmHeight();
      let filterLatLons = [];
      // 河川GeoJSONの緯度経度の格納形式によって繰り返し処理の回数が違うため
      // MultiLineString: 3層、LineString: 2層
      if (type == "MultiLineString") {
        for (var latLons of latLonList) {
          latLons.forEach((val) => {
            let tmp = this.setFilterLatLons(
              val,
              rvrNmWidthL,
              rvrNmWidthR,
              rvrNmHeight
            );

            if (tmp.length > 0) {
              filterLatLons.push(tmp);
            }
          });
        }
      } else {
        latLonList.forEach((val) => {
          let tmp = this.setFilterLatLons(
            val,
            rvrNmWidthL,
            rvrNmWidthR,
            rvrNmHeight
          );
          if (tmp.length > 0) {
            filterLatLons.push(tmp);
          }
        });
      }
      return filterLatLons;
    },
    /**
     * 地図表示内の緯度経度を返却
     */
    setFilterLatLons(val) {
      if (
        this.isOverlap([val[1], val[0]], this.mapMinLatLon, this.mapMaxLatLon)
      ) {
        return val;
      } else {
        return [];
      }
    },
    /**
     * 河川名表示位置の取得
     */
    getPostionRiverName(filterLatLons, rvrName) {
      // 河川名表示範囲の中心位置
      let center = Array.from(this.mapCenterLatLon);
      // ツールチップ表示領域_河川名表示緯度経度から左側の幅
      let rvrNmWidthL = this.setRvrNmWidthL(rvrName);
      // ツールチップ表示領域_河川名表示緯度経度から右側の幅（右のほうが若干大きいため左側と分けて用意）
      let rvrNmWidthR = this.setRvrNmWidthR(rvrName);
      // ツールチップ表示領域_高さ
      let rvrNmHeight = this.setRvrNmHeight();

      // 河川名表示緯度経度
      let closest = null;
      // 既出河川名ツールチップの表示位置リスト
      let tmp = Array.from(this.tmpRiverLatLonList);

      // 以下4パターンの格納先を用意し、上から順に優先して表示する
      // 中心から一定距離の外側かつ他河川名ツールチップと重複しない緯度経度リスト
      let filterCenterOutLatLons = [];
      // 中心から一定距離の内側かつ他河川名ツールチップと重複しない緯度経度リスト
      let filterCenterInLatLons = [];
      // 中心から一定距離の内側かつ他河川名ツールチップと重複する緯度経度リスト
      let spareCenterInLatLons = [];
      // 中心から一定距離の外側かつ他河川名ツールチップと重複する緯度経度リスト
      let spareCenterOutLatLons = [];
      if (filterLatLons.length > 0) {
        filterLatLons.forEach((val) => {
          // 中心から一定距離の内側か
          let isCenter = false;
          // 他河川名ツールチップと重複するか
          let isOverlap = false;
          // 河川名表示ツールチップの最小緯度経度
          let valMinLatLon = [val[1], val[0] - rvrNmWidthL];
          // 河川名表示ツールチップの最大緯度経度
          let valMaxLatLon = [val[1] + rvrNmHeight, val[0] + rvrNmWidthR];
          // 河川名表示ツールチップの最大緯度最小経度
          let valMaxLatMinLon = [val[1] + rvrNmHeight, val[0] - rvrNmWidthL];
          // 河川名表示ツールチップの最小緯度最大経度
          let valMinLatMaxLon = [val[1], val[0] + rvrNmWidthR];

          // 中心から一定距離の範囲内に存在するか
          if (
            // ツールチップ表示位置緯度経度
            this.isOverlap(
              [val[1], val[0]],
              this.limitMinLatLon,
              this.limitMaxLatLon
            )
          ) {
            isCenter = true;
          }
          // 既出河川名ツールチップの表示位置が存在する場合
          if (tmp.length > 0) {
            tmp.forEach((tmpVal) => {
              // 既出河川名ツールチップ領域の最小緯度経度
              let tmpMinLatLon = [tmpVal[1], tmpVal[0] - rvrNmWidthL];
              // 既出河川名ツールチップ領域の最大緯度経度
              let tmpMaxLatLon = [
                tmpVal[1] + rvrNmHeight,
                tmpVal[0] + rvrNmWidthR,
              ];

              if (
                // 河川表示名称ツールチップの最小緯度経度が既出河川表示名称ツールチップにかぶる場合
                this.isOverlap(valMinLatLon, tmpMinLatLon, tmpMaxLatLon) ||
                // 河川表示名称ツールチップの最大緯度経度が既出河川表示名称ツールチップにかぶる場合
                this.isOverlap(valMaxLatLon, tmpMinLatLon, tmpMaxLatLon) ||
                // 河川表示名称ツールチップの最小緯度最大経度が既出河川表示名称ツールチップにかぶる場合
                this.isOverlap(valMinLatMaxLon, tmpMinLatLon, tmpMaxLatLon) ||
                // 河川表示名称ツールチップの最大緯度最小経度が既出河川表示名称ツールチップにかぶる場合
                this.isOverlap(valMaxLatMinLon, tmpMinLatLon, tmpMaxLatLon)
              ) {
                isOverlap = true;
              }
            });
          }
          // 格納先の判定
          if (!isCenter && isOverlap) {
            // 中心からの一定距離の外側かつ既出河川名ツールチップと重複する場合
            spareCenterOutLatLons.push(val);
          } else if (isCenter && isOverlap) {
            // 中心からの一定距離の内側かつ既出河川名ツールチップと重複する場合
            spareCenterInLatLons.push(val);
          } else if (!isCenter && !isOverlap) {
            // 中心からの一定距離の外側かつ既出河川名ツールチップと重複しない場合
            filterCenterOutLatLons.push(val);
          } else if (isCenter && !isOverlap) {
            // 中心からの一定距離の内側かつ既出河川名ツールチップと重複しない場合
            filterCenterInLatLons.push(val);
          }
        });
      }

      if (filterCenterOutLatLons.length > 0) {
        // 中心からの一定距離の外側かつ既出河川名ツールチップと重複しない場合
        // より中心に近い位置を表示位置とする
        closest = filterCenterOutLatLons.reduce((a, b) => {
          let aDiff = this.culcDiff(center, a);
          let bDiff = this.culcDiff(center, b);
          return aDiff < bDiff ? a : b;
        });
      } else if (filterCenterInLatLons.length > 0) {
        // 中心からの一定距離の内側かつ既出河川名ツールチップと重複しない場合
        // より中心に遠い位置を表示位置とする
        closest = filterCenterInLatLons.reduce((a, b) => {
          let aDiff = this.culcDiff(center, a);
          let bDiff = this.culcDiff(center, b);
          return aDiff > bDiff ? a : b;
        });
      } else if (spareCenterOutLatLons.length > 0) {
        // 中心からの一定距離の外側かつ既出河川名ツールチップと重複する場合
        // より中心に近い位置を表示位置とする
        closest = spareCenterOutLatLons.reduce((a, b) => {
          let aDiff = this.culcDiff(center, a);
          let bDiff = this.culcDiff(center, b);
          return aDiff < bDiff ? a : b;
        });
      } else if (spareCenterInLatLons.length > 0) {
        // 中心からの一定距離の内側かつ既出河川名ツールチップと重複する場合
        // より中心に遠い位置を表示位置とする
        closest = spareCenterInLatLons.reduce((a, b) => {
          let aDiff = this.culcDiff(center, a);
          let bDiff = this.culcDiff(center, b);
          return aDiff > bDiff ? a : b;
        });
      }

      if (closest) {
        // 既出河川名ツールチップの表示位置を退避
        this.tmpRiverLatLonList.push(closest);
      }
      return closest;
    },
    /**
     * 発表情報のある河川か
     */
    isWarn(type) {
      return type == 10 || type == 11;
    },
    /**
     * 領域と緯度経度の重なり具合を判定
     */
    isOverlap(val, min, max) {
      // valが最小緯度経度×最大緯度経度内に存在するかどうか
      return (
        min[0] * this.culcDecimal <= val[0] * this.culcDecimal &&
        val[0] * this.culcDecimal <= max[0] * this.culcDecimal &&
        min[1] * this.culcDecimal <= val[1] * this.culcDecimal &&
        val[1] * this.culcDecimal <= max[1] * this.culcDecimal
      );
    },
    /**
     * ツールチップの表示範囲（左側の幅）
     */
    setRvrNmWidthL(rvrName) {
      return (
        (this.par1pxLon *
          this.culcDecimal *
          (rvrName.length * (1 / 2) - 1) *
          12) /
        this.culcDecimal
      );
    },
    /**
     * ツールチップの表示範囲（右側の幅）
     */
    setRvrNmWidthR(rvrName) {
      return (
        (this.par1pxLon *
          this.culcDecimal *
          (rvrName.length * (1 / 2) + 1.5) *
          12) /
        this.culcDecimal
      );
    },
    /**
     * ツールチップの表示範囲（高さ）
     */
    setRvrNmHeight() {
      return (this.par1pxLat * this.culcDecimal * 50) / this.culcDecimal;
    },
    /**
     * 緯度経度の差分算出
     */
    culcDiff(center, val) {
      return (
        Math.abs(center[0] * this.culcDecimal - val[1] * this.culcDecimal) +
        Math.abs(center[1] * this.culcDecimal - val[0] * this.culcDecimal)
      );
    },
    /**
     * マウスホバー時のツールチップ更新
     */
    mouseOverTooltip(event, info) {
      info.isHover = true;
      event.target._contentNode.style.fontSize = "1.2em";
      event.target._contentNode.style.zIndex = "30";
    },
    /**
     * マウスホバーを外した際のツールチップ更新
     */
    mouseLeaveTooltip(event, info) {
      info.isHover = false;
      event.target._contentNode.style.fontSize = "";
      event.target._contentNode.style.zIndex = "";
    },
    /**
     * ツールチップのオプション生成
     */
    createOptionTooltip(properties) {
      let rwLvl = this.setRwLvl(properties);
      return {
        pane: "riverNamePane",
        direction: "center",
        permanent: true,
        interactive: true,
        noWrap: true,
        opacity:
          this.mapConfig.rvrNmTooltip[rwLvl.length > 0 ? rwLvl : "default"]
            .opacity,
        sticky: false,
        className: this.setClassName(rwLvl),
      };
    },
    /**
     * ツールチップのクラス名生成
     */
    setClassName(rwLvl) {
      let className = "";
      if (rwLvl.length > 0) {
        className = "rw-tooltip-" + rwLvl;
      } else {
        className = "rw-tooltip-normal";
      }
      return className;
    },
    /**
     * 発表情報のレベル名生成
     */
    setRwLvl(info) {
      let rwLvl = "";
      if (this.isWarn(info.type)) {
        if (info.lvl == 90) {
          rwLvl = "over";
        } else if (info.lvl == 80) {
          rwLvl = "danger";
        } else if (info.lvl == 60) {
          rwLvl = "refuge";
        } else if (info.lvl == 30) {
          rwLvl = "warn";
        }
      }
      return rwLvl;
    },
    /**
     * 発表情報がある河川の場合のみ、ツールチップ押下時に画面遷移
     */
    clickRwTooltip(properties) {
      if (this.isWarn(properties.type)) {
        // 画面遷移パラメータ設定
        this.$store.commit("RiverWarn/setSelectedWarnReq", {
          cd: properties.cd,
          type: properties.type,
        });
        // 画面遷移
        if (this.$store.state.app.mode == "pc") {
          this.routerPushRw("/pc/rw");
        } else {
          this.routerPushRw("/mb/rwdetail");
        }
      }
    },
    /** 地図コンポーネントの幅取得 */
    getComponentWidth() {
      return document.getElementById("mapArea").offsetWidth;
    },
    /** 地図コンポーネントの高さ取得 */
    getComponentHeight() {
      return document.getElementById("mapArea").offsetHeight;
    },
    /** 地図上のコンポーネントのサイズ取得 */
    getComponentsSize() {
      const infoHeight =
        document.getElementById("map-emergency-info") &&
        document.getElementById("map-emergency-info").offsetHeight != 0
          ? document.getElementById("map-emergency-info").offsetHeight
          : this.infoCnt * 30;
      const sideMenuHeight = document.getElementById("side-menu")
        ? document.getElementById("side-menu").offsetHeight
        : 0;
      const settingPanelHeight = document.getElementById("setting-panel")
        ? document.getElementById("setting-panel").offsetHeight
        : 0;
      return {
        infoHeight: infoHeight,
        sideMenuHeight: sideMenuHeight,
        settingPanelHeight: settingPanelHeight,
      };
    },
    showRiverWarn(type) {
      let viewGroup = this.$store.state.map.viewGroup;
      let customInfo = WebStorage.getCustomInfo();

      if (this.getContent === "VcCustom") {
        // カスタマイズ設定編集モードの場合、カスタマイズ設定に従って予警報を表示する。
        if (type === 10) {
          // 洪水予報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs) {
            return customInfo.fldfr;
          }
        } else if (type === 11) {
          // 水位周知河川情報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs) {
            return customInfo.fldfr;
          }
          // 水防警報は未対応のためコメントアウト
          // } else if (type === 20) {
          //   // 水防警報
          //   if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls) {
          //     return customInfo.fldctl;
          //   }
        }
      } else if (
        this.getContent === "RiverwarnList" ||
        this.getContent === "Riverwarn"
      ) {
        // 予警報画面表示中は、表示設定に関わらず表示する。
        if (type === 10) {
          // 洪水予報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs) {
            return true;
          }
        } else if (type === 11) {
          // 水位周知河川情報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs) {
            return true;
          }
          // 水防警報は未対応のためコメントアウト
          // } else if (type === 20) {
          //     // 水防警報
          //     // 水防団用表示が選択されている場合
          //     if (this.$store.getters["app/isFldCtl"]) {
          //       if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls) {
          //         console.log("★6");
          //         return true;
          //       }
          //     }
        }
      } else {
        // その他の場合、表示設定に従って予警報を表示する。
        if (type === 10) {
          // 洪水予報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldfrs) {
            if (viewGroup === 5) {
              return customInfo.fldfr;
            }
            return this.viewRiverWarn;
          }
        } else if (type === 11) {
          // 水位周知河川情報
          if (this.$store.state.map.zoom >= this.mapConfig.zoomShowEvstgs) {
            if (viewGroup === 5) {
              return customInfo.fldfr;
            }
            return this.viewRiverWarn;
          }
          // 水防警報は未対応のためコメントアウト
          // } else if (type === 20) {
          //   // 水防警報
          //   // 水防団用表示が選択されている場合
          //   if (this.$store.getters["app/isFldCtl"]) {
          //     if (this.$store.state.map.zoom >= this.mapConfig.zoomShowFldctls) {
          //       if (viewGroup === 5) {
          //         return customInfo.fldctl;
          //       }
          //       return this.viewRiverWarn;
          //     }
          // }
        }
      }
      return false;
    },
    showRiverName() {
      return this.$store.state.map.viewRiver;
    },
  },
  /** 監視部 */
  watch: {
    /** 表示市町村取得時刻の更新を監視 */
    getTownsAcqTime() {
      this.getRiverGeoJson();
    },
    /** 中心地点の更新を監視 */
    center() {
      this.getRiverGeoJson();
    },
    /** zoomレベルの更新を監視 */
    getZoom() {
      this.getRiverGeoJson();
    },
    /** 河川GeoJSONの更新を監視 */
    concatRiverGeoJson() {
      this.createRiverGeoJson(this.concatRiverGeoJson);
    },
    /** ウェブストレージの変更を検知し画面更新 */
    editWebStorageDate() {
      this.updTooltip += 1;
    },
  },
};
</script>
